/**
 * Servicio de Chat IA que usa la nueva API backend con OpenAI
 * 
 * ACTUALIZACIÓN: Ahora usa OpenAI directamente en el backend en lugar de N8N
 * - El backend procesa mensajes con OpenAI GPT-4o-mini
 * - Consulta datos específicos de Supabase según la intención
 * - N8N está comentado en el backend para posible uso futuro
 * 
 * Siguiendo el mismo patrón que clientesApiService.js
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS, CHAT_API_TIMEOUT } from '../config/api';

/**
 * Endpoints del Chat IA (importados desde configuración)
 */
const CHAT_ENDPOINTS = API_ENDPOINTS.CHAT;

/**
 * Envía un mensaje al Chat IA
 * @param {string} message - Mensaje del usuario
 * @param {Object} context - Contexto adicional (sucursal, empresa, etc.)
 * @returns {Promise<Object>} - Respuesta del IA
 */
export const sendChatMessage = async (message, context = {}) => {
  try {
    console.log('🤖 [ChatService] Enviando mensaje al IA:', { 
      messageLength: message?.length,
      context 
    });
    
    if (!message || message.trim().length === 0) {
      throw new Error('El mensaje no puede estar vacío');
    }

    if (message.length > 1000) {
      throw new Error('El mensaje es muy largo (máximo 1000 caracteres)');
    }

    // Preparar datos para el backend
    const requestData = {
      message: message.trim(),
      context: {
        sucursal_id: context.sucursal_id || context.sucursalId,
        user_type: context.user_type || context.userType || 'cliente',
        empresa_nombre: context.empresa_nombre || context.empresaNombre,
        timestamp: new Date().toISOString(),
        ...context
      }
    };
    
    // Usar cliente normalizado con manejo automático de auth y timeout extendido para Chat IA
    const response = await apiHelpers.generic.post(CHAT_ENDPOINTS.MESSAGE, requestData, {
      timeout: CHAT_API_TIMEOUT
    });
    
    if (response.data) {
      const responseData = response.data;
      
      console.log('✅ [ChatService] Respuesta del IA recibida:', {
        conversationId: responseData.conversation_id,
        category: responseData.category,
        responseTime: responseData.response_time_ms,
        fullResponse: responseData
      });
      
      // Normalizar respuesta para el frontend
      return {
        success: responseData.success || true,
        response: responseData.response || responseData.message,
        conversationId: responseData.conversation_id,
        category: responseData.category || 'general',
        responseTime: responseData.response_time_ms || 0,
        timestamp: new Date().toISOString()
      };
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error('❌ [ChatService] Error al enviar mensaje:', error);
    console.error('❌ [ChatService] Error completo:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url || CHAT_ENDPOINTS.MESSAGE,
      headers: error.config?.headers
    });
    
    // Manejar errores específicos del backend
    if (error.response?.data?.error) {
      throw new Error(`Backend Error: ${error.response.data.error}`);
    }
    
    // Manejar errores de red/conexión
    if (error.response?.status >= 500) {
      throw new Error(`Error del servidor (${error.response.status}): ${error.response.statusText}`);
    }
    
    if (error.response?.status === 404) {
      throw new Error('Endpoint del chat no encontrado. Verifica la configuración del backend.');
    }
    
    if (error.response?.status === 401) {
      throw new Error('Error de autenticación. Por favor, inicia sesión nuevamente.');
    }
    
    // Errores de red
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Error de conexión. Verifica que el backend esté ejecutándose.');
    }
    
    // Fallback para otros errores
    if (error.message) {
      throw new Error(`Error: ${error.message}`);
    }
    
    throw new Error('Error al procesar tu consulta. Por favor, intenta nuevamente.');
  }
};

/**
 * Obtiene el historial de conversaciones del usuario
 * @param {Object} options - Opciones de paginación
 * @param {number} options.limit - Límite de conversaciones (default: 50)
 * @param {number} options.offset - Offset para paginación (default: 0)
 * @returns {Promise<Object>} - Historial de conversaciones
 */
export const getChatHistory = async (options = {}) => {
  try {
    console.log('📜 [ChatService] Obteniendo historial de conversaciones...');
    
    const { limit = 50, offset = 0 } = options;
    
    const response = await apiHelpers.generic.get(CHAT_ENDPOINTS.HISTORY, {
      params: { limit, offset }
    });
    
    if (response.data) {
      const historyData = response.data;
      
      console.log(`✅ [ChatService] Historial obtenido: ${historyData.conversations?.length || 0} conversaciones`);
      
      // Normalizar conversaciones para el frontend
      const conversationsNormalized = (historyData.conversations || []).map(conv => ({
        id: conv.id,
        userMessage: conv.user_message,
        aiResponse: conv.ai_response,
        category: conv.category || 'general',
        userFeedback: conv.user_feedback,
        responseHelpful: conv.response_helpful,
        responseTime: conv.response_time_ms || 0,
        createdAt: conv.created_at,
        timestamp: conv.created_at
      }));
      
      return {
        success: true,
        conversations: conversationsNormalized,
        total: historyData.total || conversationsNormalized.length,
        limit,
        offset
      };
    }
    
    return {
      success: true,
      conversations: [],
      total: 0,
      limit,
      offset
    };
  } catch (error) {
    console.error('❌ [ChatService] Error al obtener historial:', error);
    
    // En caso de error, retornar estructura vacía
    return {
      success: false,
      conversations: [],
      total: 0,
      error: error.message || 'Error al obtener el historial'
    };
  }
};

/**
 * Envía feedback sobre una respuesta del IA
 * @param {string} conversationId - ID de la conversación
 * @param {number} rating - Calificación de 1 a 5
 * @param {boolean} helpful - Si la respuesta fue útil
 * @returns {Promise<Object>} - Confirmación del feedback
 */
export const submitChatFeedback = async (conversationId, rating, helpful) => {
  try {
    console.log('📝 [ChatService] Enviando feedback:', { 
      conversationId, 
      rating, 
      helpful 
    });
    
    if (!conversationId) {
      throw new Error('ID de conversación requerido');
    }

    if (rating && (rating < 1 || rating > 5)) {
      throw new Error('La calificación debe estar entre 1 y 5');
    }
    
    const feedbackData = {
      conversation_id: conversationId,
      rating: rating || null,
      helpful: helpful !== undefined ? helpful : null
    };
    
    const response = await apiHelpers.generic.post(CHAT_ENDPOINTS.FEEDBACK, feedbackData);
    
    if (response.data) {
      console.log('✅ [ChatService] Feedback enviado exitosamente');
      
      return {
        success: response.data.success || true,
        message: response.data.message || 'Feedback registrado',
        conversationId
      };
    }
    
    throw new Error('No se pudo registrar el feedback');
  } catch (error) {
    console.error('❌ [ChatService] Error al enviar feedback:', error);
    throw new Error(error.response?.data?.error || error.message || 'Error al enviar feedback');
  }
};

/**
 * Obtiene el estado de configuración del Chat IA para el usuario actual
 * Nota: Esta función puede usarse para verificar si el chat está habilitado
 * @returns {Promise<Object>} - Estado del chat
 */
export const getChatStatus = async () => {
  try {
    console.log('🔍 [ChatService] Verificando estado del chat...');
    
    // Intentar enviar un mensaje de prueba muy corto para verificar conectividad
    const testResponse = await sendChatMessage('test', { 
      test: true,
      user_type: 'status_check'
    });
    
    return {
      enabled: true,
      connected: true,
      available: true,
      lastCheck: new Date().toISOString()
    };
  } catch (error) {
    console.warn('⚠️ [ChatService] Chat no disponible:', error.message);
    
    return {
      enabled: false,
      connected: false,
      available: false,
      error: error.message,
      lastCheck: new Date().toISOString()
    };
  }
};

/**
 * Función helper para categorizar mensajes en el frontend
 * @param {string} message - Mensaje del usuario
 * @returns {string} - Categoría del mensaje
 */
export const categorizeMessage = (message) => {
  const messageLower = message.toLowerCase();
  
  if (messageLower.includes('vehículo') || messageLower.includes('vehiculo') || messageLower.includes('auto') || messageLower.includes('patente')) {
    return 'vehiculos';
  } else if (messageLower.includes('precio') || messageLower.includes('costo') || messageLower.includes('valor') || messageLower.includes('$')) {
    return 'precios';
  } else if (messageLower.includes('cita') || messageLower.includes('hora') || messageLower.includes('agenda') || messageLower.includes('turno')) {
    return 'citas';
  } else if (messageLower.includes('mantenimiento') || messageLower.includes('servicio') || messageLower.includes('reparación')) {
    return 'mantenimientos';
  } else if (messageLower.includes('estadística') || messageLower.includes('reporte') || messageLower.includes('ingreso') || messageLower.includes('ganancia')) {
    return 'estadisticas';
  } else if (messageLower.includes('cliente') || messageLower.includes('usuario')) {
    return 'clientes';
  } else if (messageLower.includes('plan') || messageLower.includes('planes') || messageLower.includes('beneficio') || messageLower.includes('beneficios') || messageLower.includes('suscripción') || messageLower.includes('upgrade')) {
    return 'planes';
  } else {
    return 'general';
  }
};

/**
 * Función helper para generar respuestas de fallback
 * @param {string} message - Mensaje original
 * @param {string} userType - Tipo de usuario (admin/cliente)
 * @returns {string} - Respuesta de fallback
 */
export const getFallbackResponse = (message, userType = 'cliente') => {
  const category = categorizeMessage(message);
  
  const responses = {
    admin: {
      vehiculos: 'Lo siento, no puedo acceder a la información de vehículos en este momento. Por favor, intenta más tarde.',
      precios: 'No puedo consultar precios actuales. Revisa la sección de servicios en el panel administrativo.',
      estadisticas: 'Las estadísticas no están disponibles temporalmente. Intenta recargar el dashboard.',
      planes: 'No puedo consultar información de planes en este momento. Para upgrades o cambios de plan, contacta a soporte.',
      general: 'Disculpa, estoy experimentando problemas técnicos. Por favor, intenta nuevamente en unos minutos.'
    },
    cliente: {
      vehiculos: 'No puedo acceder a la información de tus vehículos ahora. Revisa la sección "Mis Vehículos".',
      precios: 'Para consultar precios actualizados, contacta directamente al taller.',
      citas: 'No puedo verificar tus citas en este momento. Revisa la sección de citas en tu perfil.',
      planes: 'No puedo mostrar información de planes ahora. Para conocer beneficios adicionales, contacta al administrador del taller.',
      general: 'Lo siento, tengo problemas técnicos temporales. Intenta preguntarme más tarde.'
    }
  };
  
  const userResponses = responses[userType] || responses.cliente;
  return userResponses[category] || userResponses.general;
};

/**
 * Hook personalizado para usar el servicio de chat
 */
export const useChatApiService = () => {
  return {
    sendChatMessage,
    getChatHistory,
    submitChatFeedback,
    getChatStatus,
    categorizeMessage,
    getFallbackResponse
  };
};

/**
 * Exportaciones por defecto para compatibilidad
 */
export default {
  sendChatMessage,
  getChatHistory,
  submitChatFeedback,
  getChatStatus,
  categorizeMessage,
  getFallbackResponse
};
